import React, { useState } from "react";
import {
  User,
  Shield,
  Activity,
  Map,
  Dumbbell,
  Users,
  Layout,
  Menu,
  Lock,
  Search,
  Zap,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  CheckCircle2,
  MousePointer2,
  Clock,
  Sparkles,
  Calendar,
  PieChart,
  Target,
  Mail,
  Plus,
  Trophy,
  Send,
  Settings,
  Edit,
  Upload,
  Globe,
  Instagram,
  MapPin,
  TrendingUp
} from "lucide-react";

// --- EXPANDED DOCUMENTATION DATA ---

const DOC_DATA = {
  athlete: {
    title: "Athlete Guide",
    icon: User,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    sections: [
      {
        id: "ath-skills",
        title: "AI Skill Development",
        heading: "The 'Modeled Athlete' Engine",
        description: "Your skill workouts aren't random. They are generated using a two-step AI process that first builds a monthly strategy based on a pro player model (e.g., Kevin Durant), and then creates daily drills to execute that strategy.",
        steps: [
          "**Set Context:** Select your Active Program and verify the 'Modeled Athlete' field (auto-filled by your profile, or editable).",
          "**Step 1: The Strategy:** You MUST generate a 'Monthly Overview' first. This analyzes your past performance and builds a 4-week roadmap.",
          "**Step 2: The Execution:** Once a monthly plan exists, use the 'Express Generate' button to create today's specific workout.",
          "**Training:** Tap the blue gradient card to open the workout details. Drills are split into 3 blocks: Review, Progression, and Game Situation."
        ],
        stickingPoint: {
          title: "Prerequisites",
          text: "You cannot generate a Daily Workout without an active Monthly Overview. If the 'Generate Daily' button fails, ensure you have clicked 'Generate Monthly Overview' for the current month first."
        },
        visual: "skills-mock"
      },
      {
        id: "ath-strength",
        title: "S&C Workouts",
        heading: "4-Week Periodization Generator",
        description: "The S&C module builds a complete 4-week strength schedule based on your available time and training frequency.",
        steps: [
          "**Configuration:** At the top, verify your 'Modeled Athlete', 'Sessions / Week' (e.g., 4), and 'Duration' (e.g., 60 mins).",
          "**Select Program:** Use the large dropdown in the center to select which sport context this strength plan is for.",
          "**Generate:** Click the primary button to build your 4-week plan. This checks your Combine goals vs. current stats.",
          "**Execute:** Your daily workout appears in the 'Today's Priority' blue card. Tap it to see the specific exercises and reps.",
          "**Calendar:** Use the 'View Full Monthly Calendar' button to see future workouts."
        ],
        stickingPoint: {
          title: "Existing Plans",
          text: "If you already have an active plan for this month, the generator will detect it and simply load your existing schedule instead of overwriting it."
        },
        visual: "strength-mock"
      },
      {
        id: "ath-marketplace",
        title: "Marketplace & Discovery",
        heading: "Finding Teams & Tryouts",
        description: "The Marketplace is your recruiting hub. It allows you to find programs by distance, sport, and age, and immediately register for upcoming tryouts.",
        steps: [
          "**Filters:** Use the top bar to filter by Sport, Program Type (Club, HS, College), and set your search radius (1-50 miles).",
          "**View Modes:** On mobile, use the floating 'Show Map / Show List' button to toggle views. Desktop shows both.",
          "**Follow:** Click 'Follow' on a program card to get on their radar.",
          "**Register:** If a program has an active tryout, you will see a Blue Date Badge. Click 'Register' to sign up immediately."
        ],
        stickingPoint: {
          title: "Follow vs. Register",
          text: "'Following' adds you to a coach's lead list. 'Registering' signs you up for a specific event (Tryout). You usually need to do both."
        },
        visual: "marketplace-mock"
      },
      {
        id: "ath-perf",
        title: "Performance Data",
        heading: "Combine & SWOT Analysis",
        description: "Understand your strengths and weaknesses through data. This section visualizes how you stack up against the competition.",
        steps: [
          "**Combine Scores:** View verified times (40yd dash, vertical, etc.) logged by coaches.",
          "**SWOT Analysis:** The AI breaks down your Strengths, Weaknesses, Opportunities, and Threats based on your recent performance."
        ],
        stickingPoint: {
          title: "Data Latency",
          text: "Combine scores must be verified by a coach. You cannot edit these manually."
        },
        visual: "swot-mock"
      },
      {
        id: "ath-profile",
        title: "Universal Profile",
        heading: "Managing Your Identity",
        description: "Your profile holds data for every sport you play, allowing you to be recruited for multiple disciplines.",
        steps: [
          "Go to Profile (User Icon).",
          "**Sport Context:** Use the dropdown to select a sport (e.g., 'Football').",
          "**Attributes:** Fill in Position and Modeled Athlete for *that specific sport*.",
          "**Save:** Click save before switching the dropdown to a different sport."
        ],
        stickingPoint: {
          title: "Context Switching",
          text: "Changing the dropdown reloads the form. Save your changes before switching sports."
        },
        visual: "profile-mock"
      }
    ]
  },
  coach: {
    title: "Coach Guide",
    icon: Zap,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    sections: [
      {
        id: "ch-dashboard",
        title: "Coach Dashboard",
        heading: "Managing Your Roster",
        description: "The Coach Dashboard provides a comprehensive view of all athletes on your teams, with performance metrics and quick access to detailed analysis.",
        steps: [
          "**Select Team:** Use the dropdown at the top to switch between teams you manage.",
          "**View Athletes:** Athletes are displayed in horizontal scrolling cards showing their overall performance percentage.",
          "**Skill & Combine Stats:** Each card shows the athlete's skill percentage and combine percentage.",
          "**Quick Actions:** Click 'Details' to see a breakdown of individual metrics, or 'SWOT' to access their strategic analysis."
        ],
        stickingPoint: {
          title: "Performance Calculation",
          text: "The overall score is calculated as the average of skill scores and combine scores. Each category shows progress toward program-defined targets."
        },
        visual: "coach-dashboard-mock"
      },
      {
        id: "ch-eval",
        title: "Live Evaluation",
        heading: "Running a Combine",
        description: "The interface is optimized for mobile usage on the field. You can input scores for an entire roster rapidly.",
        steps: [
          "Tap 'Combine' in the bottom mobile nav.",
          "Select the specific metric test (e.g., 'Pro Agility').",
          "Tap a player's name from the roster list.",
          "Enter the result. It automatically saves and updates the team leaderboard."
        ],
        visual: "combine-mock"
      },
      {
        id: "ch-swot",
        title: "SWOT Analysis Builder",
        heading: "Strategic Player Development",
        description: "Build comprehensive SWOT (Strengths, Weaknesses, Opportunities, Threats) analysis for each athlete with actionable development plans.",
        steps: [
          "**Navigate:** From the roster, click 'SWOT' on any athlete card.",
          "**Add Items:** For each quadrant, select a Category (e.g., 'Ball Handling'), then choose a specific Item.",
          "**Action Plans:** Add up to 3 action steps for each item to create development roadmaps.",
          "**Drag to Reorder:** Click and drag items to prioritize them within each category.",
          "**Edit Details:** Click any card to open the detail modal and modify the section, item name, or action steps.",
          "**Save:** Click 'Save Changes' to persist your analysis to the database."
        ],
        stickingPoint: {
          title: "Sport-Specific Sections",
          text: "Available categories and items are specific to the sport context. The system automatically loads relevant options based on the program's sport."
        },
        visual: "swot-builder-mock"
      },
      {
        id: "ch-roster",
        title: "Roster Management",
        heading: "Inviting Athletes",
        description: "Build your team by inviting players via secure links.",
        steps: [
          "Go to your Coach Profile.",
          "Tap 'Generate Invite Link'.",
          "Send this link to your athletes via group chat or email.",
          "As they accept, they will populate in your 'My Team' dashboard instantly."
        ]
      }
    ]
  },
  admin: {
    title: "Admin Guide",
    icon: Shield,
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-200",
    sections: [
      {
        id: "adm-create-program",
        title: "Creating a Program",
        heading: "Organization Setup",
        description: "Set up your sports organization with comprehensive details including demographics, location, branding, and sponsorships.",
        steps: [
          "**General Info:** Enter program name, select sport, and provide a description of your mission.",
          "**Classification:** Choose between Club/AAU, High School Program, or College Program.",
          "**Demographics:** Select age groups offered - you can specify general ages, boys ages, and girls ages separately.",
          "**Location & Contact:** Enter your address (auto-geocoded for map placement), website, and Instagram handle.",
          "**Branding:** Upload your program logo by clicking the logo placeholder. Upload multiple sponsor logos if applicable.",
          "**Submit:** Click 'Create Organization' to finalize. A default 'Varsity' team will be created automatically."
        ],
        stickingPoint: {
          title: "Auto-Generated Team",
          text: "Creating a program automatically creates a 'Varsity' team using the same sport and location. You can create additional teams afterward."
        },
        visual: "create-program-mock"
      },
      {
        id: "adm-create-team",
        title: "Creating Teams",
        heading: "Adding Team Divisions",
        description: "Create multiple teams under your program (e.g., Varsity, JV, U14, U16) with specific identities and schemes.",
        steps: [
          "**Select Program:** Choose which organization this team belongs to from the dropdown.",
          "**Team Details:** Enter team name, identity (playing style), and offensive/defensive schemes.",
          "**Logo:** Upload a team-specific logo (or inherit from program).",
          "**Location:** Set the team's primary location if different from the program address.",
          "**Age Groups:** Specify which boys and girls age groups this team serves."
        ],
        stickingPoint: {
          title: "Multiple Teams",
          text: "You can create unlimited teams under one program. Use this for age divisions, competitive tiers, or separate boys/girls squads."
        },
        visual: "create-team-mock"
      },
      {
        id: "adm-dashboard",
        title: "Admin Dashboard",
        heading: "Command Center Overview",
        description: "Your central hub for managing coaches, athletes, and viewing team performance across your entire organization.",
        steps: [
          "**Program Filter:** Select which program to view from the top dropdown (if you manage multiple).",
          "**Team Filter:** Narrow down to a specific team or view all teams combined.",
          "**Coaching Staff Section:** View active coaches and pending invites. Click the trash icon to remove or revoke.",
          "**Roster Section:** See all rostered athletes with their skill and combine percentages. Click 'Details' for breakdowns or 'SWOT' for development plans.",
          "**Invite Buttons:** Use '+Invite Coach' or '+Invite Player' to send email invitations with role assignments."
        ],
        stickingPoint: {
          title: "Filter Context",
          text: "The 'Team' filter affects both coaches and athletes displayed. Selecting 'All Teams' shows program-wide data."
        },
        visual: "admin-dashboard-mock"
      },
      {
        id: "adm-audience",
        title: "Audience & Recruiting",
        heading: "Converting Followers to Players",
        description: "The 'Audience' page tracks users who have found you in the Marketplace but aren't on a roster yet. Turn followers into team members.",
        steps: [
          "**Access:** Click the 'Users' icon in the header to open the Audience page.",
          "**Search & Filter:** Use the search bar to find specific athletes by name or school. Filter by grade level.",
          "**Review Followers:** Each card shows the athlete's grade, school, location (city/state only for privacy), height, and weight.",
          "**Expand Card:** Click the '+Invite to Team' button to see a list of your teams.",
          "**Send Invite:** Select a team to send a formal roster invitation. The button will show 'Sent' with a checkmark once complete.",
          "**Track Status:** Invites appear as 'Pending' until the athlete accepts."
        ],
        stickingPoint: {
          title: "Privacy Protection",
          text: "Addresses are truncated to city and state only. Full details become available once a player accepts an invitation."
        },
        visual: "audience-mock"
      },
      {
        id: "adm-tryouts",
        title: "Tryout Management",
        heading: "Scheduling & Registration",
        description: "Create tryout events, track registrations, and manage athlete sign-ups through a dedicated dashboard.",
        steps: [
          "**Create Tryout:** Click '+Create' from the tryouts dashboard. Fill in date, start/end times, location, description, grades, and gender.",
          "**Time Zones:** The system converts your local time inputs to UTC for storage and displays them correctly in your timezone.",
          "**View Registrations:** Switch to the 'Athletes Registered' tab to see who has signed up for each event.",
          "**Filter:** Use the 'Upcoming/All' toggle to show only future events or include past tryouts.",
          "**Edit/Delete:** Modify existing tryouts or remove them using the action buttons on each card."
        ],
        stickingPoint: {
          title: "Registration Data",
          text: "Athlete names and emails appear in the registration list. If data is missing, ensure the athlete's profile is complete."
        },
        visual: "tryout-dashboard-mock"
      },
      {
        id: "adm-drills",
        title: "Program Drills & Goals",
        heading: "Setting Skill and Combine Targets",
        description: "Define program-wide skill drills and combine tests with target scores. Assign drills to specific positions and mark timed events.",
        steps: [
          "**Select Program:** Choose which organization's goals to manage from the dropdown.",
          "**Add Skill Drill:** Click '+Add Skill Drill'. Enter name, description, target score, and select applicable positions.",
          "**Add Combine Drill:** Click '+Add Combine Drill'. Same process as skills but typically for athletic testing (e.g., 40-yard dash).",
          "**Mark Timed Drills:** Toggle 'Is Timed?' for events where lower scores are better (e.g., sprint times). A stopwatch badge will appear.",
          "**Position Filtering:** Drills only appear for athletes whose position matches the selected tags.",
          "**Inline Editing:** Click on any drill card to edit the name inline. Use the pencil icon to open the full modal editor.",
          "**Delete:** Hover over a card and click the X icon to remove a drill permanently."
        ],
        stickingPoint: {
          title: "Timed vs. Non-Timed",
          text: "For timed drills, the system inverts percentage calculations so that beating the target time results in a higher score percentage."
        },
        visual: "drills-admin-mock"
      },
      {
        id: "adm-profile",
        title: "Program Profile Management",
        heading: "Updating Organization Details",
        description: "Edit program information, update branding, and manage multiple organizations from a centralized profile interface.",
        steps: [
          "**Access:** Navigate to the Admin Profile page from your account menu.",
          "**Switch Programs:** If you manage multiple organizations, use the dropdown to select which one to edit.",
          "**Edit Mode:** Click 'Edit Profile' to unlock all text fields for modification.",
          "**Logo Upload:** Click the logo image at any time to upload a new one. Changes save immediately.",
          "**Location Updates:** Edit the address and click 'Fetch' to re-geocode the coordinates for accurate map placement.",
          "**Demographics:** Update age groups by editing the comma-separated values (e.g., 'U10, U12, U14').",
          "**Save:** Click 'Save Changes' to commit all text edits to the database."
        ],
        stickingPoint: {
          title: "Image vs. Text Updates",
          text: "Logo uploads save instantly upon selection. All other fields require clicking 'Save Changes' to persist."
        },
        visual: "admin-profile-mock"
      },
      {
        id: "adm-org",
        title: "Organization Control",
        heading: "Managing Multiple Teams",
        description: "Switch between different squads (e.g., Varsity vs JV) without logging out.",
        steps: [
          "Use the top header dropdown to switch contexts.",
          "Ensure you are in the correct context before creating Tryout Events or viewing Combine data.",
          "Invite coaches to specific teams to delegate management."
        ]
      }
    ]
  }
};

// --- VISUAL MOCK COMPONENTS ---

const VisualMock = ({ type }) => {
  if (type === "skills-mock") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto overflow-hidden relative">
         <div className="flex gap-2 mb-4">
             <div className="flex-1 bg-gray-50 border border-gray-100 rounded-lg p-2">
                 <span className="text-[9px] text-gray-400 font-bold uppercase block">Modeled Athlete</span>
                 <div className="flex items-center gap-1 text-xs font-bold text-gray-800">
                    <User size={10} /> Kevin Durant
                 </div>
             </div>
             <div className="w-16 bg-gray-50 border border-gray-100 rounded-lg p-2">
                 <span className="text-[9px] text-gray-400 font-bold uppercase block">Time</span>
                 <div className="flex items-center gap-1 text-xs font-bold text-gray-800">
                    <Clock size={10} /> 60m
                 </div>
             </div>
         </div>
         <div className="mb-4">
            <div className="w-full bg-slate-900 text-white rounded-xl p-3 shadow-md flex items-center justify-between">
                <div>
                    <span className="block text-[10px] text-blue-400 font-bold uppercase tracking-wider">Step 2: Execute</span>
                    <span className="font-bold text-sm">Generate Daily Workout</span>
                </div>
                <Sparkles size={16} className="text-blue-400" />
            </div>
         </div>
         <div>
             <span className="text-xs font-bold text-gray-800 mb-2 block">Today's Session</span>
             <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-3 text-white shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 p-2 opacity-10">
                   <Activity size={48} />
                </div>
                <div className="relative z-10">
                    <span className="inline-block bg-white/20 px-2 py-0.5 rounded text-[10px] font-semibold mb-2">Oct 24</span>
                    <h3 className="text-sm font-bold leading-tight mb-1">Ball Handling & Attack</h3>
                    <p className="text-blue-100 text-[10px]">3 Blocks • Review, Progression, Game Sim</p>
                </div>
             </div>
         </div>
      </div>
    );
  }

  if (type === "strength-mock") {
    return (
      <div className="bg-gray-100 p-4 rounded-xl w-full max-w-sm mx-auto overflow-hidden space-y-4">
        <div className="bg-gray-50/50 rounded-xl p-2 border border-gray-200/50 space-y-2">
          <div className="flex gap-2">
             <div className="flex-1 bg-white p-2 rounded-lg border border-gray-100 shadow-sm flex items-center gap-2">
                <div className="bg-gray-100 p-1.5 rounded"><User size={12} className="text-gray-500"/></div>
                <div><span className="text-[8px] text-gray-400 font-bold uppercase block">Modeled</span><span className="text-[10px] font-bold">LeBron</span></div>
             </div>
             <div className="flex-1 bg-white p-2 rounded-lg border border-gray-100 shadow-sm flex items-center gap-2">
                <div className="bg-gray-100 p-1.5 rounded"><Activity size={12} className="text-gray-500"/></div>
                <div><span className="text-[8px] text-gray-400 font-bold uppercase block">Freq</span><span className="text-[10px] font-bold">4/wk</span></div>
             </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-lg border border-blue-50 relative overflow-hidden">
           <div className="relative z-10 text-center space-y-3">
              <div>
                 <label className="text-[10px] font-bold text-gray-500 mb-1 block">Active Program</label>
                 <div className="bg-blue-50 border border-blue-100 text-gray-800 text-sm font-bold rounded-xl py-2 px-4 flex justify-between items-center">
                    Nuvio Elite <ChevronDown size={14} className="text-blue-500"/>
                 </div>
              </div>
              <div className="bg-slate-900 text-white rounded-xl py-2 text-xs font-bold shadow-md">
                 Generate Plan
              </div>
              <div className="bg-white border border-blue-100 text-blue-600 rounded-xl py-2 text-xs font-bold flex items-center justify-center gap-1">
                 <Calendar size={12}/> View Calendar
              </div>
           </div>
        </div>

        <div>
           <h3 className="text-xs font-bold text-gray-800 mb-2 px-1">Today's Session</h3>
           <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 shadow-lg text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 p-2 opacity-10"><Activity size={60}/></div>
              <div className="relative z-10">
                 <div className="flex justify-between items-start mb-2">
                    <span className="bg-white/20 px-2 py-0.5 rounded text-[9px] font-bold uppercase backdrop-blur-sm">Today's Priority</span>
                    <div className="bg-white/20 p-1 rounded-full"><ChevronRight size={12}/></div>
                 </div>
                 <h3 className="text-lg font-extrabold leading-tight mb-1">Explosive Power</h3>
                 <div className="flex gap-2 text-[10px] text-blue-100 font-medium items-center">
                    <Clock size={10}/> Load: 3 x 8
                 </div>
              </div>
           </div>
        </div>
      </div>
    );
  }

  if (type === "marketplace-mock") {
    return (
      <div className="bg-gray-50 rounded-xl border border-gray-200 shadow-sm w-full max-w-sm mx-auto overflow-hidden flex flex-col h-72">
          <div className="bg-white p-3 border-b border-gray-200 space-y-2 z-10">
            <div className="relative">
               <Search size={12} className="absolute left-2 top-1.5 text-gray-400"/>
               <div className="h-6 bg-gray-50 border border-gray-200 rounded-md w-full pl-7 flex items-center text-[10px] text-gray-400">Search programs...</div>
            </div>
            <div className="flex gap-2">
               <div className="h-6 bg-white border border-gray-200 rounded-md w-1/3 flex items-center px-2 text-[9px] text-gray-600 font-medium">Basketball</div>
               <div className="flex-1 flex items-center gap-2 px-1">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                     <div className="w-2/3 h-full bg-blue-500"></div>
                  </div>
                  <span className="text-[9px] font-bold text-gray-500 whitespace-nowrap">35 mi</span>
               </div>
            </div>
          </div>
          <div className="p-3 bg-gray-50 flex-1 overflow-hidden relative">
             <div className="bg-white p-3 rounded-xl border border-blue-500 ring-1 ring-blue-500 shadow-md mb-2 relative">
                <div className="flex justify-between items-start mb-1">
                   <h3 className="font-bold text-gray-900 text-xs">Nuvio Elite</h3>
                   <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">3.2 mi</span>
                </div>
                <p className="text-[9px] text-gray-500 mb-3">Basketball • Club Program</p>
                <div className="flex items-center gap-2 mb-2">
                   <div className="px-2 py-1 rounded-md border border-gray-200 text-[9px] font-bold text-gray-600 bg-white shadow-sm">Follow</div>
                   <div className="px-2 py-1 rounded-md border border-gray-200 text-[9px] font-bold text-gray-600 bg-white shadow-sm">Website</div>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
                    <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">📅 Next: Oct 24</span>
                    <button className="bg-blue-600 text-white text-[9px] font-bold px-2 py-1 rounded shadow-sm hover:bg-blue-700">Register</button>
                </div>
                <div className="flex gap-1 mt-2">
                   <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                   <div className="w-1.5 h-1.5 rounded-full bg-pink-400"></div>
                </div>
             </div>
             <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-4 py-1.5 rounded-full text-[10px] font-bold flex items-center gap-1.5 shadow-lg border border-white/20 z-20">
                <Map size={10} /> Show Map
             </div>
          </div>
        </div>
    );
  }

  if (type === "profile-mock") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto">
        <div className="flex items-center justify-between mb-4">
             <div className="w-10 h-10 rounded-full bg-gray-200 border-2 border-white shadow-sm"></div>
             <button className="text-[10px] bg-gray-900 text-white px-3 py-1 rounded">Save Changes</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Active Sport Context</label>
            <div className="flex items-center justify-between p-2 mt-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 font-bold">
              Basketball <ChevronDown size={14} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
              <div className="p-2 bg-gray-50 rounded border border-gray-100">
                  <span className="text-[9px] text-gray-400 block uppercase">Position</span>
                  <span className="text-xs font-medium text-gray-800">Point Guard</span>
              </div>
              <div className="p-2 bg-gray-50 rounded border border-gray-100">
                  <span className="text-[9px] text-gray-400 block uppercase">Height</span>
                  <span className="text-xs font-medium text-gray-800">6'2"</span>
              </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === "swot-mock") {
    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto">
             <div className="flex items-center gap-2 mb-3">
                 <PieChart size={16} className="text-purple-600"/>
                 <span className="text-xs font-bold text-gray-700">SWOT Analysis</span>
             </div>
             <div className="grid grid-cols-2 gap-2 mb-3">
                 <div className="bg-green-50 p-2 rounded border border-green-100">
                     <span className="text-[9px] font-bold text-green-700 uppercase">Strengths</span>
                     <p className="text-[9px] text-green-800 mt-1">Explosive first step.</p>
                 </div>
                 <div className="bg-red-50 p-2 rounded border border-red-100">
                     <span className="text-[9px] font-bold text-red-700 uppercase">Weaknesses</span>
                     <p className="text-[9px] text-red-800 mt-1">Left hand finishing.</p>
                 </div>
             </div>
        </div>
    );
  }
    
  if (type === "combine-mock") {
      return (
        <div className="bg-slate-900 rounded-xl border border-slate-700 shadow-lg p-4 w-full max-w-sm mx-auto text-white">
          <div className="flex justify-between items-center mb-4">
              <span className="text-xs text-blue-400 font-bold tracking-widest uppercase">Combine Mode</span>
              <Activity size={16} className="text-blue-500" />
          </div>
          <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-slate-800 rounded border border-slate-700">
                  <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-slate-600"></div>
                      <span className="text-xs font-medium text-slate-300">Athlete 1</span>
                  </div>
                  <div className="flex items-center gap-2">
                      <span className="text-xs font-bold font-mono text-white">4.42s</span>
                  </div>
              </div>
          </div>
        </div>
      );
    }
      
  if (type === "audience-mock") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 w-full max-w-sm mx-auto">
          <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-indigo-600 uppercase">Audience (Leads)</span>
              <Users size={14} className="text-indigo-400"/>
          </div>
          <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-indigo-50 rounded border border-indigo-100">
                  <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-indigo-200"></div>
                      <div>
                          <span className="text-xs font-bold text-indigo-900 block">John Doe</span>
                          <span className="text-[9px] text-indigo-500">Followed 2h ago</span>
                      </div>
                  </div>
                  <button className="text-[9px] bg-white border border-indigo-200 text-indigo-700 px-2 py-1 rounded shadow-sm">Invite</button>
              </div>
          </div>
      </div>
    );
  }

  // NEW MOCKS FOR ADMIN FEATURES

  if (type === "create-program-mock") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
          <Shield size={16} className="text-blue-600" />
          <span className="text-sm font-bold text-gray-900">Create Organization</span>
        </div>
        
        <div className="space-y-2">
          <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
            <span className="text-[8px] text-gray-400 font-bold uppercase block mb-1">Program Name</span>
            <span className="text-xs font-bold text-gray-800">Shoreline Elite Basketball</span>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-2 text-center">
              <span className="text-[9px] font-bold text-blue-700">Club/AAU</span>
              <CheckCircle2 size={10} className="mx-auto mt-1 text-blue-600" />
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-center">
              <span className="text-[9px] font-medium text-gray-500">High School</span>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-center">
              <span className="text-[9px] font-medium text-gray-500">College</span>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="w-16 h-16 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center">
              <Upload size={20} className="text-gray-400" />
            </div>
            <div className="flex-1 bg-gray-50 rounded-lg p-2 border border-gray-100">
              <span className="text-[8px] text-gray-400 font-bold uppercase block">Location</span>
              <span className="text-[10px] text-gray-700">Shoreline, WA</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === "create-team-mock") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-gray-100">
          <span className="text-sm font-bold text-gray-900">Create Team</span>
          <Trophy size={16} className="text-amber-600" />
        </div>
        
        <div className="space-y-2">
          <input 
            type="text" 
            placeholder="Team Name (e.g., Varsity)" 
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs"
            disabled
          />
          
          <select className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs" disabled>
            <option>Select Program</option>
          </select>

          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Offensive Scheme" className="bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-[10px]" disabled />
            <input placeholder="Defensive Scheme" className="bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-[10px]" disabled />
          </div>
        </div>
      </div>
    );
  }

  if (type === "admin-dashboard-mock") {
    return (
      <div className="bg-gray-50 rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">Roster & Athletes</h3>
          <Plus size={16} className="text-blue-600" />
        </div>

        <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
          <div className="flex items-start gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-blue-100"></div>
            <div className="flex-1">
              <h4 className="font-bold text-sm text-gray-900">John Smith</h4>
              <span className="text-[10px] text-blue-600 font-bold bg-blue-50 px-2 py-0.5 rounded">85%</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[10px] mb-2">
            <div className="bg-gray-50 p-1.5 rounded">
              <span className="text-gray-500">Skill:</span>
              <span className="font-bold ml-1">82%</span>
            </div>
            <div className="bg-gray-50 p-1.5 rounded">
              <span className="text-gray-500">Combine:</span>
              <span className="font-bold ml-1">88%</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="flex-1 bg-gray-100 text-gray-700 text-[10px] font-bold py-1.5 rounded">Details</button>
            <button className="flex-1 bg-blue-600 text-white text-[10px] font-bold py-1.5 rounded">SWOT</button>
          </div>
        </div>
      </div>
    );
  }

  if (type === "tryout-dashboard-mock") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-gray-100">
          <span className="text-sm font-bold text-gray-900">Tryout Events</span>
          <Calendar size={16} className="text-blue-600" />
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h4 className="font-bold text-sm text-gray-900">Spring Tryouts</h4>
              <span className="text-[10px] text-blue-600">March 15, 2024 • 3:00 PM</span>
            </div>
            <span className="bg-blue-600 text-white text-[9px] font-bold px-2 py-1 rounded">12 Registered</span>
          </div>
          <p className="text-[10px] text-gray-600 mb-2">Main Gym • Grades 9-12</p>
          <div className="flex gap-2">
            <button className="flex-1 bg-white border border-blue-200 text-blue-700 text-[10px] font-bold py-1.5 rounded">Edit</button>
            <button className="flex-1 bg-white border border-red-200 text-red-600 text-[10px] font-bold py-1.5 rounded">Delete</button>
          </div>
        </div>
      </div>
    );
  }

  if (type === "drills-admin-mock") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-gray-100">
          <span className="text-sm font-bold text-gray-900">Program Drills</span>
          <Target size={16} className="text-green-600" />
        </div>
        
        <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-bold text-sm text-gray-900">40 Yard Dash</h4>
            <div className="flex gap-1">
              <Edit size={12} className="text-gray-400" />
            </div>
          </div>
          <p className="text-[10px] text-gray-500 mb-2">Speed and acceleration test</p>
          <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold text-gray-500">Target:</span>
              <span className="text-sm font-bold text-gray-900">4.5s</span>
            </div>
            <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
              <Clock size={10} />
              <span className="text-[9px] font-bold">TIMED</span>
            </div>
          </div>
          <div className="flex gap-1 mt-2">
            <span className="text-[9px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded border border-blue-100 font-bold">WR</span>
            <span className="text-[9px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded border border-blue-100 font-bold">CB</span>
          </div>
        </div>
      </div>
    );
  }

  if (type === "admin-profile-mock") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto space-y-3">
        <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
          <div className="w-16 h-16 rounded-xl bg-gray-200 border-2 border-white shadow-sm"></div>
          <div className="flex-1">
            <h3 className="font-bold text-sm text-gray-900">Nuvio Elite</h3>
            <span className="text-[10px] text-gray-500">Basketball • Club/AAU</span>
          </div>
          <Edit size={14} className="text-gray-400" />
        </div>

        <div className="space-y-2">
          <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
            <span className="text-[8px] text-gray-400 font-bold uppercase block">Website</span>
            <span className="text-[10px] text-blue-600">www.nuvioelite.com</span>
          </div>
          
          <div className="flex gap-2">
            <div className="flex-1 bg-gray-50 rounded-lg p-2 border border-gray-100">
              <span className="text-[8px] text-gray-400 font-bold uppercase block">Location</span>
              <span className="text-[10px] text-gray-700">Shoreline, WA</span>
            </div>
            <div className="flex-1 bg-gray-50 rounded-lg p-2 border border-gray-100">
              <span className="text-[8px] text-gray-400 font-bold uppercase block">Ages</span>
              <span className="text-[10px] text-gray-700">U12-U18</span>
            </div>
          </div>
        </div>

        <button className="w-full bg-blue-600 text-white text-xs font-bold py-2 rounded-lg">
          Save Changes
        </button>
      </div>
    );
  }

  if (type === "coach-dashboard-mock") {
    return (
      <div className="bg-gray-50 rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900">My Roster</h3>
          <Zap size={16} className="text-amber-600" />
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2">
          {[1, 2].map((i) => (
            <div key={i} className="min-w-[200px] bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-bold text-sm text-gray-900">Player {i}</h4>
                  <span className="text-[10px] text-gray-500">Point Guard</span>
                </div>
                <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-1 rounded">#23</span>
              </div>
              
              <div className="space-y-1 mb-3">
                <div className="flex justify-between text-[10px]">
                  <span className="text-gray-600">Skill</span>
                  <span className="font-bold">85%</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-gray-600">Combine</span>
                  <span className="font-bold">78%</span>
                </div>
              </div>

              <div className="w-full bg-gray-100 rounded-full h-1.5 mb-2">
                <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: "82%" }}></div>
              </div>

              <div className="flex gap-2">
                <button className="flex-1 bg-gray-100 text-gray-700 text-[10px] font-bold py-1.5 rounded">Details</button>
                <button className="flex-1 bg-blue-600 text-white text-[10px] font-bold py-1.5 rounded">SWOT</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "swot-builder-mock") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 w-full max-w-sm mx-auto space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-gray-100">
          <span className="text-sm font-bold text-gray-900">SWOT Builder</span>
          <TrendingUp size={16} className="text-purple-600" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="bg-green-50 border border-green-200 rounded-lg p-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-green-800 uppercase">Strengths</span>
              <span className="text-[9px] bg-white px-1.5 py-0.5 rounded text-green-700 font-bold">2</span>
            </div>
            <div className="bg-white border border-green-100 rounded p-2 mb-1">
              <span className="text-[8px] text-green-600 uppercase font-bold block">Ball Handling</span>
              <span className="text-[10px] text-gray-800 font-medium">Crossover Speed</span>
            </div>
            <div className="bg-white border border-green-100 rounded p-2">
              <span className="text-[8px] text-green-600 uppercase font-bold block">Speed</span>
              <span className="text-[10px] text-gray-800 font-medium">First Step</span>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-red-800 uppercase">Weaknesses</span>
              <span className="text-[9px] bg-white px-1.5 py-0.5 rounded text-red-700 font-bold">1</span>
            </div>
            <div className="bg-white border border-red-100 rounded p-2">
              <span className="text-[8px] text-red-600 uppercase font-bold block">Finishing</span>
              <span className="text-[10px] text-gray-800 font-medium">Left Hand Layups</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <span className="text-[9px] font-bold text-gray-400 uppercase block mb-2">Add New Strength</span>
          <select className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-[10px] mb-2">
            <option>Select Category...</option>
          </select>
          <select className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-[10px] mb-2">
            <option>Select Item...</option>
          </select>
          <button className="w-full bg-gray-900 text-white text-[10px] font-bold py-1.5 rounded">
            + Add Item
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// --- MAIN COMPONENT ---

export default function NuvioDocumentation() {
  const [activeRole, setActiveRole] = useState("athlete");
  const [activeSectionId, setActiveSectionId] = useState(DOC_DATA.athlete.sections[0].id);
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  // Derive current data
  const currentRoleData = DOC_DATA[activeRole];
  const activeSection = currentRoleData.sections.find(s => s.id === activeSectionId) || currentRoleData.sections[0];

  return (
    <div className="flex h-screen bg-gray-50 text-slate-800 font-sans overflow-hidden">
      
      {/* MOBILE OVERLAY */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 flex flex-col ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 border-b border-gray-100 bg-slate-50/50">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                <Layout className="text-blue-600" /> Nuvio Docs
            </h1>
            <p className="text-xs text-slate-400 font-medium mt-1 uppercase tracking-wider">User Manual v3.0</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-8">
            <div>
                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 px-2">Select User Role</h3>
                <div className="grid gap-2">
                    {Object.keys(DOC_DATA).map((roleKey) => {
                        const role = DOC_DATA[roleKey];
                        const isActive = activeRole === roleKey;
                        const Icon = role.icon;
                        
                        return (
                            <button
                                key={roleKey}
                                onClick={() => {
                                    setActiveRole(roleKey);
                                    setActiveSectionId(role.sections[0].id);
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-bold transition-all ${
                                    isActive 
                                    ? `${role.bgColor} ${role.color} shadow-sm ring-1 ring-inset ${role.borderColor}` 
                                    : "text-slate-500 hover:bg-slate-50 border border-transparent"
                                }`}
                            >
                                <Icon size={18} />
                                {role.title}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                 <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 px-2">Feature Walkthroughs</h3>
                 <div className="space-y-1">
                    {currentRoleData.sections.map((section) => {
                        const isSectionActive = activeSectionId === section.id;
                        return (
                            <button
                                key={section.id}
                                onClick={() => {
                                    setActiveSectionId(section.id);
                                    setSidebarOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center justify-between group ${
                                    isSectionActive
                                    ? "bg-slate-100 text-slate-900 font-bold"
                                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                                }`}
                            >
                                {section.title}
                                {isSectionActive && <ChevronRight size={14} className="text-slate-400" />}
                            </button>
                        )
                    })}
                 </div>
            </div>

        </div>
        
        <div className="p-4 border-t border-gray-100">
             <div className="flex items-center gap-2 text-[10px] text-gray-400">
                 <Lock size={10} />
                 <span>Internal Documentation</span>
             </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 flex flex-col h-full relative overflow-hidden bg-white">
        
        <header className="flex-shrink-0 bg-white border-b border-gray-200 p-4 md:px-8 flex items-center justify-between sticky top-0 z-10">
             <div className="flex items-center gap-3">
                 <button onClick={() => setSidebarOpen(true)} className="md:hidden text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                     <Menu size={24} />
                 </button>
                 <div className={`hidden md:flex p-2 rounded-lg ${currentRoleData.bgColor} ${currentRoleData.color}`}>
                     <currentRoleData.icon size={20} />
                 </div>
                 <div>
                    <h2 className="text-sm md:text-lg font-bold text-slate-900 leading-none">
                        {activeSection.title}
                    </h2>
                    <span className="text-[10px] md:text-xs text-slate-500 mt-1 block">
                        {currentRoleData.title} &bull; {activeSection.heading}
                    </span>
                 </div>
             </div>
        </header>

        <div className="flex-1 overflow-y-auto bg-slate-50/50">
            <div className="max-w-5xl mx-auto p-4 md:p-10 pb-24">
                
                <div className="mb-10 bg-white p-6 md:p-8 rounded-2xl border border-gray-200 shadow-sm">
                    <h1 className="text-2xl md:text-3xl font-black text-slate-900 mb-3">{activeSection.heading}</h1>
                    <p className="text-base md:text-lg text-slate-600 leading-relaxed">{activeSection.description}</p>
                </div>

                <div className="grid lg:grid-cols-2 gap-8 items-start">
                    
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                            <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-6 text-sm uppercase tracking-wide">
                                <MousePointer2 size={16} className="text-blue-500" />
                                Action Steps
                            </h3>
                            <div className="space-y-6 relative">
                                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gray-100"></div>
                                
                                {activeSection.steps.map((step, idx) => (
                                    <div key={idx} className="relative flex gap-4">
                                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white ring-2 ring-slate-100 text-slate-500 font-bold text-xs flex items-center justify-center z-10">
                                            {idx + 1}
                                        </div>
                                        <div className="pt-0.5 text-sm text-slate-600 leading-relaxed">
                                            {step.split("**").map((part, i) => 
                                                i % 2 === 1 ? <strong key={i} className="text-slate-900 font-semibold">{part}</strong> : part
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {activeSection.stickingPoint && (
                            <div className="bg-amber-50 rounded-xl p-5 border border-amber-100 flex gap-4 shadow-sm">
                                <div className="flex-shrink-0 text-amber-600 mt-0.5">
                                    <AlertTriangle size={20} />
                                </div>
                                <div>
                                    <h4 className="font-bold text-amber-900 text-sm mb-1">
                                        Pro Tip: {activeSection.stickingPoint.title}
                                    </h4>
                                    <p className="text-xs text-amber-800 leading-relaxed">
                                        {activeSection.stickingPoint.text}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lg:sticky lg:top-6">
                        <div className="bg-white p-6 md:p-8 rounded-3xl shadow-xl border border-gray-100 relative overflow-hidden group">
                             <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
                             
                             <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6 text-center">
                                 UI Simulation
                             </h4>
                             
                             <div className="transform group-hover:scale-[1.02] transition-transform duration-500 ease-out">
                                {activeSection.visual && <VisualMock type={activeSection.visual} />}
                             </div>
                             
                             <div className="mt-8 text-center">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-[10px] font-medium text-slate-500">
                                    <CheckCircle2 size={10} className="text-green-500" /> Live Component Preview
                                </span>
                             </div>
                        </div>
                    </div>

                </div>

            </div>
        </div>

      </main>
    </div>
  );
}